/**
 * FlahaSOIL v2 — ReportChartsSection (Phase 10D).
 *
 * Integrates Phase 10C-D charts into the professional report.
 */
import { Grid, Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useEffect, useState } from "react";
import type {
  ScientificAnalysisResponse,
  GetSoilTestResponse,
  ProfessionalReportDTO,
  ReportChartScope,
} from "@flaha/shared-types";
import { reportLevelScope } from "@flaha/shared-types";

import { getApiClient } from "../../../../services/apiClientProvider";
import {
  TextureTriangleChart,
  WaterRetentionCurveChart,
  BaseSaturationChart,
  CationBalanceChart,
  SalinitySodicityChart,
  textureTriangleChartAdapter,
  waterRetentionChartAdapter,
  baseSaturationChartAdapter,
  cationBalanceChartAdapter,
  salinitySodicityChartAdapter,
} from "../../charts";

export interface ReportChartsSectionProps {
  soilTestId: string;
  reportSnapshot: ProfessionalReportDTO;
  /** Override chart suite; defaults from declared test level on snapshot. */
  chartScope?: ReportChartScope;
}

export function ReportChartsSection({
  soilTestId,
  reportSnapshot,
  chartScope: chartScopeProp,
}: ReportChartsSectionProps) {
  const levelScope = reportLevelScope(
    reportSnapshot.cover.declaredTestLevel ?? reportSnapshot.cover.testLevel
  );
  const chartScope = chartScopeProp ?? levelScope.chartScope;
  const textureOnly = chartScope === "texture_only";
  const [analysis, setAnalysis] = useState<ScientificAnalysisResponse | null>(null);
  const [fullTest, setFullTest] = useState<GetSoilTestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const client = getApiClient();
    
    Promise.all([
      client.getScientificAnalysis(soilTestId),
      client.getSoilTest(soilTestId),
    ])
      .then(([a, t]) => {
        if (!cancelled) {
          setAnalysis(a);
          setFullTest(t);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError("Failed to load scientific charts.");
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [soilTestId]);

  if (loading) return <CircularProgress size={24} sx={{ my: 2 }} />;
  if (error || !analysis || !fullTest) return null;

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {analysis.texture && (
        <Grid item xs={12} md={textureOnly ? 12 : 6}>
          <Box className="no-break" data-chart-snapshot="textureTriangle">
            <Typography variant="subtitle2" gutterBottom align="center">USDA Texture Triangle</Typography>
            <TextureTriangleChart chartData={textureTriangleChartAdapter(analysis.texture)} />
          </Box>
        </Grid>
      )}
      {!textureOnly && analysis.waterRetention && (
        <Grid item xs={12} md={6}>
          <Box className="no-break" data-chart-snapshot="waterRetention">
            <Typography variant="subtitle2" gutterBottom align="center">Water Retention Curve</Typography>
            <WaterRetentionCurveChart chartData={waterRetentionChartAdapter(analysis.waterRetention)} />
          </Box>
        </Grid>
      )}
      {!textureOnly && fullTest.chemistryResult && (
        <Grid item xs={12} md={6}>
          <Box className="no-break" data-chart-snapshot="baseSaturation">
            <Typography variant="subtitle2" gutterBottom align="center">Base Saturation</Typography>
            <BaseSaturationChart chartData={baseSaturationChartAdapter(fullTest.chemistryResult)} />
          </Box>
        </Grid>
      )}
      {!textureOnly && analysis.structure && (
        <Grid item xs={12} md={6}>
          <Box className="no-break" data-chart-snapshot="cationBalance">
            <Typography variant="subtitle2" gutterBottom align="center">Cation Balance</Typography>
            <CationBalanceChart chartData={cationBalanceChartAdapter(analysis.structure)} />
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
